import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ROLES, ROLE_LABELS, VIEWS, type Perm, type Role } from '@ouradent/shared';
import { queryClient } from '../lib/data';
import { supabase } from '../lib/supabase';
import { useSession } from '../auth/SessionProvider';
import { CloseX, useDrawer } from '../ui/kit';

type Member = {
  id: string;
  fullName: string;
  email: string;
  role: Role;
  external: boolean;
};

type AuditRow = {
  id: number;
  action: string;
  entity: string;
  entity_id: string | null;
  at: string;
};

// Same grouping as the prototype's Team & access screen: every perm maps
// 1:1 to a VIEWS entry, and view.group is already Sales / Outreach / Admin.
const PERM_GROUPS = (() => {
  const order: string[] = [];
  const byGroup = new Map<string, Perm[]>();
  for (const view of VIEWS) {
    if (!byGroup.has(view.group)) {
      byGroup.set(view.group, []);
      order.push(view.group);
    }
    byGroup.get(view.group)!.push(view.perm);
  }
  return order.map((group) => [group, byGroup.get(group)!] as const);
})();

const initials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2);

function describeAction(row: AuditRow): string {
  switch (row.action) {
    case 'member.invited':
      return 'Member invited';
    case 'member.reactivated':
      return 'Member reactivated';
    case 'member.removed':
      return 'Access removed';
    case 'role.changed':
      return 'Role changed';
    case 'member.restored':
      return 'Member brought back';
    case 'member.approved':
      return 'Account approved';
    case 'member.rejected':
      return 'Account refused';
    case 'member.renamed':
      return 'Member renamed';
    case 'member.external':
      return 'External flag changed';
    case 'owner.granted':
      return 'Made owner';
    case 'owner.removed':
      return 'Owner removed';
    case 'perm.grant':
      return 'Permission granted';
    case 'perm.revoke':
      return 'Permission revoked';
    default:
      return row.action;
  }
}

export function Team() {
  const { me } = useSession();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [removed, setRemoved] = useState<Member[]>([]);
  const [restoreRole, setRestoreRole] = useState<Record<string, Role>>({});
  const [perms, setPerms] = useState<Record<string, Set<Perm>>>({});
  const [log, setLog] = useState<AuditRow[]>([]);
  const { open, drawer } = useDrawer();
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    const [{ data: profiles }, { data: gone }, { data: permRows }, { data: logRows }] =
      await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, email, role, external')
          .eq('active', true)
          .order('full_name'),
        supabase
          .from('profiles')
          .select('id, full_name, email, role, external')
          .eq('active', false)
          .order('full_name'),
        supabase.from('permissions').select('user_id, perm'),
        supabase
          .from('audit_log')
          .select('id, action, entity, entity_id, at')
          .order('at', { ascending: false })
          .limit(20),
      ]);

    setRemoved(
      (gone ?? []).map((p) => ({
        id: p.id as string,
        fullName: p.full_name as string,
        email: p.email as string,
        role: p.role as Role,
        external: p.external as boolean,
      })),
    );
    setMembers(
      (profiles ?? []).map((p) => ({
        id: p.id as string,
        fullName: p.full_name as string,
        email: p.email as string,
        role: p.role as Role,
        external: p.external as boolean,
      })),
    );

    const byUser: Record<string, Set<Perm>> = {};
    for (const row of permRows ?? []) {
      const uid = row.user_id as string;
      (byUser[uid] ??= new Set<Perm>()).add(row.perm as Perm);
    }
    setPerms(byUser);

    // Empty for anyone who isn't an owner: audit_log_read is is_owner()-only
    // even when they have the team permission (docs/specs/team-and-permissions.md).
    setLog((logRows ?? []) as AuditRow[]);
  }, []);

  useEffect(() => {
    if (!drawer) void reload();
  }, [reload, drawer]);

  async function changeRole(userId: string, role: Role) {
    setError('');
    const { error: rpcError } = await supabase.rpc('change_role', { p_user: userId, p_role: role });
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    await reload();
  }

  async function removeAccess(userId: string, name: string) {
    if (!window.confirm(`Remove all access for ${name}? They are signed out right away.`)) return;
    setError('');
    const { error: rpcError } = await supabase.rpc('remove_access', { p_user: userId });
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    await reload();
  }

  async function manage(fn: string, args: Record<string, unknown>, ask?: string) {
    if (ask && !window.confirm(ask)) return;
    setError('');
    const { error: rpcError } = await supabase.rpc(fn, args);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    await reload();
  }

  async function passwordLink(email: string) {
    setError('');
    const { error: e } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    setError(e ? e.message : '');
    if (!e) window.alert(`A link to set a new password was sent to ${email}.`);
  }

  async function togglePerm(userId: string, perm: Perm, on: boolean) {
    setError('');
    const { error: writeError } = on
      ? await supabase.from('permissions').delete().eq('user_id', userId).eq('perm', perm)
      : await supabase.from('permissions').insert({ user_id: userId, perm });
    if (writeError) {
      setError(writeError.message);
      return;
    }
    await reload();
  }

  if (!members) {
    return <p className="text-muted">Loading…</p>;
  }

  return (
    <>
      <header className="mb-8 flex flex-wrap items-center justify-between gap-5">
        <div>
          <h1 className="text-[34px] font-bold leading-[1.05] tracking-[-0.035em] md:text-[44px]">
            Team &amp; <span className="grad-text">access</span>
          </h1>
          <p className="mt-1.5 text-lg text-muted">
            Pick a role, switch single areas on or off, remove people and bring them back.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => open({ type: 'invite' })}>
          Invite member
        </button>
      </header>

      {error && (
        <p className="mb-5 rounded-nav bg-red-tint p-3 text-sm text-red" role="alert">
          {error}
        </p>
      )}

      <Requests onChange={reload} />

      <div className="flex flex-col gap-4">
        {members.map((member) => {
          const isOwner = member.role === 'owner';
          const isMe = member.id === me?.id;
          const memberPerms = perms[member.id] ?? new Set<Perm>();

          return (
            <section key={member.id} className="card p-6" data-testid={`member-${member.id}`}>
              <div className="flex flex-wrap items-center gap-3.5">
                <span
                  className="av s"
                  style={{ background: 'var(--purple)', color: '#fff' }}
                  aria-hidden="true"
                >
                  {initials(member.fullName)}
                </span>
                <div className="min-w-[160px] flex-1">
                  <b className="text-lg">{member.fullName}</b>{' '}
                  {member.external && (
                    <span className="rounded-pill bg-orange-tint px-2 py-0.5 text-xs font-semibold text-orange-ink">
                      External
                    </span>
                  )}{' '}
                  {isMe && (
                    <span className="rounded-pill bg-canvas px-2 py-0.5 text-xs font-semibold text-muted">
                      You
                    </span>
                  )}
                </div>

                {isMe || isOwner ? (
                  <span className="rounded-pill bg-teal-tint px-3 py-1 text-xs font-semibold text-teal-ink">
                    {ROLE_LABELS[member.role]}, full access
                  </span>
                ) : (
                  <>
                    <select
                      aria-label={`Role for ${member.fullName}`}
                      value={member.role}
                      onChange={(e) => void changeRole(member.id, e.target.value as Role)}
                      className="w-auto max-w-[240px] rounded-nav border border-line px-3 py-2 text-sm"
                    >
                      {ROLES.filter((r) => r !== 'owner').map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn"
                      onClick={() => void removeAccess(member.id, member.fullName)}
                    >
                      Remove access
                    </button>
                  </>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-soft">{member.email}</span>
                <button
                  className="btn sm ghost"
                  onClick={() => {
                    const name = window.prompt('Name', member.fullName);
                    if (name && name.trim() && name.trim() !== member.fullName)
                      void manage('set_member_name', { p_user: member.id, p_name: name.trim() });
                  }}
                >
                  Rename
                </button>
                <button className="btn sm ghost" onClick={() => void passwordLink(member.email)}>
                  Send password link
                </button>
                {!isOwner && (
                  <label className="flex items-center gap-1.5 text-soft">
                    <input
                      type="checkbox"
                      checked={member.external}
                      onChange={(e) =>
                        void manage('set_member_external', {
                          p_user: member.id,
                          p_external: e.target.checked,
                        })
                      }
                    />
                    External (sees only their own screen)
                  </label>
                )}
                {me?.role === 'owner' && !isMe && (
                  <button
                    className="btn sm"
                    onClick={() =>
                      void manage(
                        'set_owner',
                        { p_user: member.id, p_owner: !isOwner },
                        isOwner
                          ? `Take ownership from ${member.fullName}? They become a Sales member.`
                          : `Make ${member.fullName} an owner? An owner sees and changes everything, also Team & access.`,
                      )
                    }
                  >
                    {isOwner ? 'Remove as owner' : 'Make owner'}
                  </button>
                )}
              </div>

              {!isOwner && (
                <div className="mt-5 flex flex-col gap-3">
                  {PERM_GROUPS.map(([group, groupPerms]) => (
                    <div key={group} className="flex flex-wrap items-center gap-2">
                      <span className="w-20 shrink-0 text-xs font-semibold uppercase tracking-wide text-soft">
                        {group}
                      </span>
                      {groupPerms.map((perm) => {
                        const on = memberPerms.has(perm);
                        const label = VIEWS.find((v) => v.perm === perm)?.label ?? perm;
                        return (
                          <button
                            key={perm}
                            type="button"
                            aria-pressed={on}
                            onClick={() => void togglePerm(member.id, perm, on)}
                            className={[
                              'rounded-pill border px-3 py-1.5 text-xs font-semibold',
                              on
                                ? 'border-purple bg-purple-tint text-purple'
                                : 'border-line text-soft',
                            ].join(' ')}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {removed.length > 0 && (
        <section className="card mt-6 p-6" data-testid="removed-members">
          <h2 className="text-lg font-bold">Removed</h2>
          <p className="mt-1 text-muted">They cannot sign in. Bring someone back with a role.</p>
          <div className="mt-3 flex flex-col gap-2">
            {removed.map((m) => (
              <div
                key={m.id}
                className="flex flex-wrap items-center gap-3 border-t border-line py-2 first:border-t-0"
              >
                <div className="min-w-[180px] flex-1">
                  <b>{m.fullName}</b>
                  <div className="text-sm text-soft">{m.email}</div>
                </div>
                <select
                  aria-label={`Role for ${m.fullName}`}
                  value={restoreRole[m.id] ?? 'sales'}
                  onChange={(e) =>
                    setRestoreRole({ ...restoreRole, [m.id]: e.target.value as Role })
                  }
                  className="w-auto max-w-[240px] rounded-nav border border-line px-3 py-2 text-sm"
                >
                  {ROLES.filter((r) => r !== 'owner').map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
                <button
                  className="btn"
                  onClick={() =>
                    void manage('restore_member', {
                      p_user: m.id,
                      p_role: restoreRole[m.id] ?? 'sales',
                    })
                  }
                >
                  Bring back
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="card mt-6 p-6">
        <h2 className="text-lg font-bold">Access log</h2>
        {log.length === 0 ? (
          <p className="mt-3 text-muted">No activity yet.</p>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            {log.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between gap-3 border-t border-line py-2 text-sm first:border-t-0 first:pt-0"
              >
                <span>{describeAction(row)}</span>
                <span className="shrink-0 text-xs text-soft">
                  {new Date(row.at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

async function inviteErrorMessage(error: unknown): Promise<string> {
  if (error && typeof error === 'object' && 'context' in error) {
    const context = (error as { context?: Response }).context;
    if (context) {
      try {
        const body = (await context.json()) as { error?: string };
        switch (body.error) {
          case 'forbidden':
            return 'You do not have permission to invite members.';
          case 'already_member':
            return 'This person is already a member.';
          case 'invalid_body':
            return 'Check the name, email and role.';
          default:
            return body.error ?? 'Could not send the invite.';
        }
      } catch {
        return 'Could not send the invite.';
      }
    }
  }
  return 'Could not send the invite.';
}

export function InviteDrawer() {
  const { close } = useDrawer();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('sales');
  const [external, setExternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!fullName.trim()) {
      setError('Add a name.');
      return;
    }
    setSending(true);
    setError('');
    const { error: invokeError } = await supabase.functions.invoke('invite-member', {
      body: { email, fullName, role, external },
    });
    if (invokeError) {
      setSending(false);
      setError(await inviteErrorMessage(invokeError));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: ['team'] });
    close();
  }

  const field = 'rounded-nav border border-line px-4 py-3 text-base text-ink';
  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">Invite member</h2>
          <div className="sm">They get an email with a sign-in link.</div>
        </div>
        <CloseX />
      </div>
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <div className="dbody">
          <div className="panel grid2">
            <label className="field" htmlFor="invite-name">
              <span>Name</span>
              <input
                id="invite-name"
                className={field}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </label>
            <label className="field" htmlFor="invite-email">
              <span>Email</span>
              <input
                id="invite-email"
                type="email"
                required
                className={field}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field" htmlFor="invite-role">
              <span>Role</span>
              <select
                id="invite-role"
                className={field}
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
              >
                {ROLES.filter((r) => r !== 'owner').map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" htmlFor="invite-external">
              <span>External?</span>
              <select
                id="invite-external"
                className={field}
                value={external ? '1' : '0'}
                onChange={(e) => setExternal(e.target.value === '1')}
              >
                <option value="0">No, team member</option>
                <option value="1">Yes, external</option>
              </select>
            </label>
          </div>
          {error && (
            <p style={{ color: 'var(--red)' }} role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="dfoot">
          <button className="btn primary big" type="submit" disabled={sending}>
            {sending ? 'Sending…' : 'Send invite'}
          </button>
          <button className="btn ghost" type="button" onClick={close}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}

type Request = { user_id: string; email: string; full_name: string; created_at: string };

// People who created an account and wait for a role. Refusing removes the account.
function Requests({ onChange }: { onChange: () => Promise<void> }) {
  const [rows, setRows] = useState<Request[]>([]);
  const [roles, setRoles] = useState<Record<string, Role>>({});
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('access_requests')
      .select('user_id, email, full_name, created_at')
      .order('created_at');
    setRows((data ?? []) as Request[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: 'approve_access' | 'reject_access', r: Request) {
    setError('');
    const role = roles[r.user_id] ?? 'sales';
    const args =
      fn === 'approve_access'
        ? { p_user: r.user_id, p_role: role, p_external: role === 'caller' }
        : { p_user: r.user_id };
    const { error: e } = await supabase.rpc(fn, args);
    if (e) {
      setError(e.message);
      return;
    }
    await load();
    await onChange();
  }

  if (rows.length === 0 && !error) return null;

  return (
    <section className="card mb-6 p-6" data-testid="access-requests">
      <h2 className="text-lg font-bold">Waiting for approval</h2>
      <p className="mt-1 text-muted">
        These people created an account. Pick a role to let them in.
      </p>
      {error && (
        <p className="mt-3 rounded-nav bg-red-tint p-3 text-sm text-red" role="alert">
          {error}
        </p>
      )}
      <div className="mt-4 flex flex-col gap-3">
        {rows.map((r) => (
          <div key={r.user_id} className="flex flex-wrap items-center gap-3">
            <div className="min-w-[180px] flex-1">
              <b>{r.full_name}</b>
              <div className="text-sm text-muted">{r.email}</div>
            </div>
            <select
              aria-label={`Role for ${r.full_name}`}
              value={roles[r.user_id] ?? 'sales'}
              onChange={(e) => setRoles({ ...roles, [r.user_id]: e.target.value as Role })}
              className="rounded-nav border border-line px-3 py-2"
            >
              {ROLES.filter((x) => x !== 'owner').map((x) => (
                <option key={x} value={x}>
                  {ROLE_LABELS[x]}
                </option>
              ))}
            </select>
            <button className="btn btn-primary" onClick={() => void act('approve_access', r)}>
              Approve
            </button>
            <button
              className="btn"
              onClick={() => {
                if (window.confirm(`Refuse ${r.email}? The account is deleted.`))
                  void act('reject_access', r);
              }}
            >
              Refuse
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
